import { useEffect, useRef, useState } from 'react';

import { useScrollHorizontal } from '@repo/hooks';
import { getServiceName } from '@repo/utils';
import ReactECharts from 'echarts-for-react';
import EChartsReact from 'echarts-for-react';
import styled from 'styled-components';

import { useCalculateChartWidth } from './hooks/useCalculateChartWidth';

export type Metrics = {
  name: string;
  value: number;
};

interface EBarChartProps {
  metrics: Metrics[];
}

const EBarChart = ({ metrics }: EBarChartProps) => {
  const chartRef = useRef<EChartsReact>(null);
  // 가로 스크롤을 위한 훅 사용
  const {
    containerRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
  } = useScrollHorizontal();

  // 항목당 너비 설정 (각 항목당 43px 너비)
  const barWidth = 8;
  const { chartWidth } = useCalculateChartWidth(containerRef, metrics, { minWidth: 400 });

  // 최대값 찾기
  const maxValue = Math.max(...metrics.map((item) => item.value));

  // 텍스트 길이에 맞게 동적으로 너비를 계산 (최소 80px 보장)
  const value = maxValue.toLocaleString();
  const textWidth = value.length * 7 + 24;
  const markWidth = Math.max(textWidth, 80);
  const markHeight = 29; // 원래 높이 36px * 0.8

  const [highestValueMarkUrl, setHighestValueMarkUrl] = useState<string | null>(null);
  useEffect(() => {
    setHighestValueMarkUrl(null);
    if (maxValue === 0) return;

    const url = createBlobUrlFromHighestValueMarkSvg(value, markWidth, markHeight);
    setHighestValueMarkUrl(url);
    // 컴포넌트 언마운트 시 Blob URL 해제
    return () => {
      if (url) URL.revokeObjectURL(url);
      setHighestValueMarkUrl(null);
    };
  }, [value, metrics]);

  // ECharts 옵션 설정
  const getOption = () => ({
    tooltip: {
      trigger: 'item',
      // 기본 스타일 제거
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderColor: 'transparent',
      shadowColor: 'transparent',
      textStyle: {
        color: '#FFFFFF', // 텍스트 색상 설정
        fontFamily: 'Pretendard, sans-serif',
        fontSize: 11,
        fontWeight: 500,
      },
      formatter: (params: any) => {
        if (params.value === maxValue) {
          return '';
        }
        return createCustomTooltip(params.value.toLocaleString());
      },
      // 툴팁 위치를 막대 바로 위에 설정
      position: (point: number[], params: any, dom: HTMLElement, rect: any, size: any) => {
        const x = rect.x + rect.width / 2 - size.contentSize[0] / 2;
        const y = rect.y - size.contentSize[1] - 3;
        return [x, y];
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: metrics.map((item) => item.name),
      axisLine: {
        lineStyle: {
          color: metrics.length > 0 ? '#C4C6C9' : 'transparent',
        },
      },
      axisLabel: {
        color: '#8A8A8A', // Y축 라벨 색상
        fontFamily: 'Pretendard', // 폰트 패밀리
        fontSize: 11, // 폰트 크기
        fontStyle: 'normal', // 폰트 스타일
        fontWeight: 600, // 폰트 굵기
        lineHeight: 16, // 줄 높이
      },
      axisTick: {
        show: false, // X축 눈금 제거
      },
      splitLine: {
        show: false, // X축에 그리드 라인 제거
      },
    },
    yAxis: {
      type: 'value',
      axisLine: {
        lineStyle: {
          color: metrics.length > 0 ? '#C4C6C9' : 'transparent', // X축 테두리 색상
        },
      },
      axisLabel: {
        color: '#8A8A8A', // X축 라벨 색상
        fontFamily: 'Pretendard', // 폰트 패밀리
        fontSize: 11, // 폰트 크기
        fontStyle: 'normal', // 폰트 스타일
        fontWeight: 600, // 폰트 굵기
        lineHeight: 16, // 줄 높이
      },
    },
    series: [
      {
        type: 'bar',
        data: metrics.map((item) => ({
          value: item.value,
          itemStyle: {
            // color: item.value === maxValue ? '#00C3DD' : '#B0EAF4',
            color: getBarColor(item.value, maxValue),
            borderRadius: item.value > 0 ? [3, 3, 0, 0] : [0, 0, 3, 3], // 값이 양수면 상단 모서리 둥글게 처리 / 음수면 하단 모서리 둥글게 처리
          },
        })),
        barCategoryGap: '0%', // 막대 간격을 없애 컨테이너 너비에 맞춤
        barWidth,
        markPoint: {
          symbol: `image://${highestValueMarkUrl}`,
          symbolSize: [markWidth, 36],
          data: [
            {
              type: 'max',
              name: 'Highest Value',
              symbolOffset: [0, -20], // 마커 위치 조정
            },
          ],
          label: {
            show: false, // 라벨 비활성화
          },
        },
      },
    ],
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
  });

  // useEffect(() => {
  //   // 최대값이 화면에 중앙에 오도록 하는 코드
  //   if (!containerRef.current || metrics.length === 0) return;

  //   // 최대값의 인덱스 찾기
  //   const maxValueIndex = metrics.findIndex((item) => item.value === maxValue);

  //   // 차트의 전체 너비
  //   const totalWidth = containerRef.current.scrollWidth;
  //   // 보이는 영역의 너비
  //   const viewportWidth = containerRef.current.clientWidth;

  //   // 각 막대의 위치 계산 (왼쪽 여백 + 막대 인덱스 * 막대 간격)
  //   const barPosition = (totalWidth / metrics.length) * maxValueIndex;

  //   // 최대값이 오른쪽 절반에 있는 경우
  //   if (maxValueIndex > metrics.length / 2) {
  //     // 최대값 막대가 보이도록 오른쪽으로 스크롤
  //     containerRef.current.scrollLeft = Math.min(totalWidth - viewportWidth, barPosition - viewportWidth / 2);
  //   } else {
  //     // 최대값이 왼쪽 절반에 있는 경우 왼쪽으로 스크롤
  //     containerRef.current.scrollLeft = Math.max(0, barPosition - viewportWidth / 2);
  //   }
  // }, [metrics, maxValue, containerRef]);

  return (
    <SChartContainer
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div style={{ width: `${chartWidth}px` }}>
        <ReactECharts ref={chartRef} option={getOption()} style={{ height: '304px', width: `${chartWidth}px` }} />
      </div>
    </SChartContainer>
  );
};

export default EBarChart;

// 스타일링된 컨테이너로 가로 스크롤 적용
const SChartContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

// 최대값에 파란 말풍선을 띄우기 위함임
const createBlobUrlFromHighestValueMarkSvg = (value: string, width: number, height: number): string => {
  const tailSize = 5; // 꼬리 크기
  const serviceName = getServiceName();
  const backgroundColor = serviceName === 'bubblechat' ? '#00C3DD' : '#FF5776';

  // SVG 문자열 생성
  const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height + tailSize}">
      <path
        d="
          M14,0 
          H${width - 14} 
          A14,14 0 0 1 ${width},14 
          V${height - 14} 
          A14,14 0 0 1 ${width - 14},${height} 
          H${width / 2 + tailSize} 
          L${width / 2},${height + tailSize} 
          L${width / 2 - tailSize},${height} 
          H14 
          A14,14 0 0 1 0,${height - 14} 
          V14 
          A14,14 0 0 1 14,0
        "
        fill="${backgroundColor}"
      />
      <g transform="translate(${width / 2}, ${height / 2})" text-anchor="middle">
        <svg width="13" height="13" x="-32" y="-6.5" viewBox="0 0 20 20" fill="none">
          <path
            d="M2 17.1579C2 16.6928 2.37702 16.3158 2.84211 16.3158H17.1579C17.623 16.3158 18 16.6928 18 17.1579C18 17.623 17.623 18 17.1579 18H2.84211C2.37702 18 2 17.623 2 17.1579ZM2 6.34064C2 5.55309 2.86812 5.07461 3.53399 5.49515L6 7.05263L9.21596 2.99037C9.61633 2.48464 10.3837 2.48464 10.7841 2.99037L14 7.05263L16.466 5.49515C17.1319 5.07461 18 5.55309 18 6.34064V13.6316C18 14.1839 17.5523 14.6316 17 14.6316H3C2.44772 14.6316 2 14.1839 2 13.6316V6.34064Z"
            fill="#FFFFFF"
          />
        </svg>
        <text x="8" y="4" font-size="11" font-weight="700" fill="#FFFFFF" font-family="Arial, sans-serif">
          ${value}
        </text>
      </g>
    </svg>
  `;

  // Blob 생성
  const blob = new Blob([svgString], { type: 'image/svg+xml' });

  // Blob URL 생성
  return URL.createObjectURL(blob);
};

const createCustomTooltip = (amountFormat: string): string => {
  return `
    <div style="
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: 29px;
    ">
      <div style="
        background: rgba(0, 0, 0, 0.8);
        padding: 0px 6px;
        border-radius: 4px;
        height: 25px;
        display: flex;
        align-items: center;
      ">
        <span style="
          color: white;
          font-family: Pretendard;
          font-size: 11px;
          font-style: normal;
          font-weight: 500;
          line-height: 17px;
        ">
          ${amountFormat}
        </span>
      </div>      
      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="4" viewBox="0 0 7 4" fill="none">
        <g>
          <path d="M3.5 4L0 0H7L3.5 4Z" fill="#333333" />
        </g>
        <defs>
          <filter
            id="filter0_d_18454_2425"
            x="-3"
            y="-2"
            width="13"
            height="10"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="1" />
            <feGaussianBlur stdDeviation="1" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.121569 0 0 0 0 0.184314 0 0 0 0 0.27451 0 0 0 0.12 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_18454_2425" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_18454_2425" result="shape" />
          </filter>
        </defs>
      </svg>
    </div>
  `;
};

const getBarColor = (currentValue: number, maxValue: number) => {
  const serviceName = getServiceName();

  if (currentValue < 0) {
    return '#FFA7A7';
  }
  if (currentValue === maxValue) {
    return serviceName === 'bubblechat' ? '#00C3DD' : '#FF5776';
  } else {
    return serviceName === 'bubblechat' ? '#B0EAF4' : '#FFE3E6';
  }
};
